import React, { useState, useEffect } from 'react';
import { useSiteContext } from '../../context/SiteContext';
import RLinkLogo from '../common/RLinkLogo';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import WaitingRoomChat from '../waitingRoom/WaitingRoomChat';

const WaitingForApproval = ({
  onLeaveQueue,
  isWaitingForApproval = false,  // true when actually waiting for host approval
  isRejected = false,            // true when denied entry
  rejectionReason = null,        // optional reason for rejection
  hostIsOnline = false,          // true when host has joined/rejoined the meeting
  socket = null,                 // waiting room socket for chat
  meetingId = null,              // meeting ID for chat
  odId = null,                   // participant ID for chat
  odName = null,                 // participant name for chat
}) => {
  const { meetingRole, meetingSettings } = useSiteContext();
  const { branding: wlBranding } = useWhiteLabel();
  const [countdown, setCountdown] = useState(3);

  const isHost = meetingRole === "moderator" || meetingRole === "co-host";
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Countdown timer when rejected
  useEffect(() => {
    if (isRejected && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isRejected, countdown]);

  // Determine which content to show
  const getContent = () => {
    if (isRejected) {
      return {
        title: "Entry Denied",
        titleClass: "text-red-500",
        message: rejectionReason || "The host has denied your request to join this meeting.",
        subText: `You will be redirected in ${countdown} second${countdown !== 1 ? 's' : ''}...`,
        showAnimation: false,
        animationColor: 'red'
      };
    }

    if (isHost) {
      return {
        title: "Connecting...",
        titleClass: "text-white",
        message: "Please wait while we connect you to the meeting",
        subText: "You will be automatically admitted once connected",
        showAnimation: true,
        animationColor: 'brand'
      };
    }

    if (isWaitingForApproval) {
      return {
        title: meetingSettings?.waitingRoomTitle || "Please Wait",
        titleClass: "text-white",
        message: meetingSettings?.waitingRoomMessage || "The host will let you in soon",
        subText: meetingSettings?.waitingRoomSubText || "You will be notified when you're admitted",
        showAnimation: true,
        animationColor: 'brand'
      };
    }

    // Default: Just connecting (no approval required or initial connection)
    return {
      title: "Connecting...",
      titleClass: "text-transparent bg-clip-text bg-gradient-to-r from-[#6a1fbf] to-[#00c853]",
      message: "Please wait while we connect you to the meeting",
      subText: "This should only take a moment",
      showAnimation: true,
      animationColor: 'brand'
    };
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001233] to-[#6a1fbf] flex items-center justify-center p-4">
      <div className={`bg-white/5 backdrop-blur-xl border rounded-2xl p-8 max-w-md w-full mx-4 text-center ${
        isRejected ? 'border-red-500/30' : 'border-white/10'
      }`}>
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <RLinkLogo size="lg" companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
        </div>

        {/* Animation */}
        {content.showAnimation ? (
          <div className="flex justify-center items-center gap-2 mb-6">
            <div className="w-3 h-3 bg-[#6a1fbf] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-[#8e01da] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-[#00c853] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        ) : (
          <div className="flex justify-center items-center mb-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
        )}

        {/* Status Message */}
        <h1 className={`text-2xl font-bold mb-4 ${content.titleClass}`}>
          {content.title}
        </h1>

        <p className="text-white text-lg mb-2">
          {content.message}
        </p>

        <p className="text-gray-400 text-sm mb-6">
          {content.subText}
        </p>

        {/* Host online indicator */}
        {hostIsOnline && isWaitingForApproval && !isRejected && (
          <div className="flex items-center justify-center gap-2 mb-4 text-green-400 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            Host is online
          </div>
        )}

        {/* Meeting Info - Only show when waiting for approval */}
        {meetingSettings?.roomName && !isHost && isWaitingForApproval && !isRejected && (
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-medium">{meetingSettings.roomName}</p>
                {meetingSettings?.hostName && (
                  <p className="text-gray-400 text-sm">Host: {meetingSettings.hostName}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leave Queue Button - Only for non-hosts waiting for approval */}
        {!isHost && onLeaveQueue && isWaitingForApproval && !isRejected && (
          <button
            onClick={onLeaveQueue}
            className="px-6 py-2.5 text-sm font-medium text-gray-300 bg-white/10 hover:bg-white/20 rounded-xl transition-colors border border-white/10"
          >
            Leave Queue
          </button>
        )}
      </div>

      {/* Waiting Room Chat - Only for participants waiting for approval */}
      {!isHost && isWaitingForApproval && !isRejected && socket && (
        <WaitingRoomChat
          socket={socket}
          meetingId={meetingId}
          odId={odId}
          odName={odName}
          isOpen={isChatOpen}
          onToggle={() => setIsChatOpen((prev) => !prev)}
        />
      )}
    </div>
  );
};

export default WaitingForApproval;
